import { EClusterErrors, ISingleCluster } from '../entities/IClusters';
import { EClusterPermissionsBase } from '../entities/IPermissions';

export const CLUSTER_CREATION_RETRY_DELAY_MS = 3000;
export const CLUSTER_CREATION_MAX_ATTEMPTS = 20;
/** GET /Clusters/{id} when the user cannot access the resource. */
export const CLUSTER_ACCESS_DENIED_ERROR_CODE = 1001;
/** GET /Clusters/{id} when the cluster is not found (not creating). */
export const CLUSTER_NOT_FOUND_ERROR_CODE = 2000;

export const createCreationInProgressPlaceholder = (clusterId: string): ISingleCluster => ({
    id: clusterId,
    errorCode: EClusterErrors.CreationInProgress
} as ISingleCluster);

export const isClusterCreationInProgress = (cluster: { errorCode?: number } | undefined): boolean =>
    cluster?.errorCode === EClusterErrors.CreationInProgress;

export const isClusterReadyForDisplay = (
    cluster: { id?: string; errorCode?: number; presentation?: unknown; permissions?: unknown } | undefined,
    expectedId: string | undefined
): boolean => {
    if (!cluster?.id || !expectedId || cluster.id !== expectedId) {
        return false;
    }
    if (clusterHasPreviewPermission(cluster.permissions) || !!cluster.presentation) {
        return true;
    }
    return !isClusterCreationInProgress(cluster);
};

export const clusterHasPreviewPermission = (permissions: unknown): boolean => {
    if (!Array.isArray(permissions)) {
        return false;
    }
    return permissions.some((permission: unknown) =>
        (typeof permission === 'string' ? permission : (permission as { name?: string })?.name) === EClusterPermissionsBase.ClusterPreview
    );
};

export const getHttpErrorStatus = (err: unknown): number | undefined => {
    if (!err || typeof err !== 'object') {
        return undefined;
    }
    const error = err as { status?: number; response?: { status?: number } };
    return error.status ?? error.response?.status;
};

export const getHttpErrorCode = (err: unknown): number | undefined => {
    if (!err || typeof err !== 'object') {
        return undefined;
    }
    const error = err as { data?: { errorCode?: number; ErrorCode?: number } };
    return error.data?.errorCode ?? error.data?.ErrorCode;
};

export const isClusterCreationInProgressFailure = (err: unknown): boolean =>
    getHttpErrorCode(err) === EClusterErrors.CreationInProgress;

export const isClusterUnavailableFailure = (err: unknown): boolean => {
    if (isClusterCreationInProgressFailure(err)) {
        return false;
    }
    const status = getHttpErrorStatus(err);
    const errorCode = getHttpErrorCode(err);
    return status === 403
        || status === 404
        || errorCode === CLUSTER_ACCESS_DENIED_ERROR_CODE
        || errorCode === CLUSTER_NOT_FOUND_ERROR_CODE;
};

export const getClusterIdFromAssignToClusterCompleted = (message: unknown): string | undefined => {
    if (typeof message === 'string' && message.length > 0) {
        return message;
    }
    if (!message || typeof message !== 'object') {
        return undefined;
    }
    const payload = message as Record<string, unknown>;
    if (typeof payload.id === 'string' && payload.id.length > 0) {
        return payload.id;
    }
    if (typeof payload.clusterId === 'string' && payload.clusterId.length > 0) {
        return payload.clusterId;
    }
    const cluster = payload.cluster;
    if (cluster && typeof cluster === 'object' && typeof (cluster as { id?: unknown }).id === 'string') {
        const clusterId = (cluster as { id: string }).id;
        return clusterId.length > 0 ? clusterId : undefined;
    }
    const additional = payload.additionalInformation;
    if (additional && typeof additional === 'object' && typeof (additional as { clusterId?: unknown }).clusterId === 'string') {
        const clusterId = (additional as { clusterId: string }).clusterId;
        return clusterId.length > 0 ? clusterId : undefined;
    }
    return undefined;
};
